import React, { useState, useEffect, useCallback } from 'react';
import { 
  ICheckboxComponent,
  IFormControlChange,
  IFormValidationErrors
} from '../df-form-preview-interfaces';
import { DfFormErrorMsg } from '../df-form-error-msg';
import { DfFormComments } from '../df-form-comments';

interface DfFormCheckboxProps {
  id: string;
  properties: ICheckboxComponent;
  validationErrors?: IFormValidationErrors;
  formValue?: string[];
  readonly?: boolean;
  disabled?: boolean;
  touchedFields?: Record<string, boolean>;
  formSubmitted?: boolean;
  mode?: 'edit' | 'preview' | 'test';
  onValueChange?: (change: IFormControlChange) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  className?: string;
  hideLabel?: boolean;
  showCommentsInPreview?: boolean;
}

export const DfFormCheckbox: React.FC<DfFormCheckboxProps> = ({
  id,
  properties,
  validationErrors = {},
  formValue = [],
  readonly = false,
  disabled = false,
  touchedFields = {},
  formSubmitted = false,
  mode = 'preview',
  onValueChange,
  onBlur,
  onFocus,
  className = '',
  hideLabel = false,
  showCommentsInPreview = false
}) => {
  const [isTouched, setIsTouched] = useState<boolean>(false);
  const [comment, setComment] = useState<string>('');
  const [showCommentInput, setShowCommentInput] = useState<boolean>(false);
  
  // Use formValue directly instead of local state to avoid conflicts
  const selectedValues = Array.isArray(formValue) ? formValue : [];

  // Initialize comment from properties when in preview mode (for form submissions)
  useEffect(() => {
    if (mode === 'preview' && showCommentsInPreview && properties?.basic?.comments) {
      setComment(properties.basic.comments);
    }
  }, [mode, showCommentsInPreview, properties]);

  // Validate field
  const validateField = useCallback((values: string[]): { isValid: boolean; errors: Record<string, any> } => {
    const errors: Record<string, any> = {};
    let isValid = true;

    // Required validation
    if (properties?.validation?.required && values.length === 0) {
      errors.required = true;
      isValid = false;
    }

    return { isValid, errors };
  }, [properties]);

  // Handle value changes
  const handleValueChange = useCallback((newValues: string[]) => {
    
    if (onValueChange) {
      const validation = validateField(newValues);
      const change: IFormControlChange = {
        id,
        value: newValues,
        isValid: validation.isValid,
        errors: validation.errors,
        comments: comment
      };
      onValueChange(change);
    }
  }, [id, onValueChange, validateField, comment, selectedValues]);

  // Helper function to check if a value should show red chip (fail/no)
  const shouldShowRedChip = (value: string): boolean => {
    const lowerValue = value.toLowerCase();
    return lowerValue === 'no' || lowerValue === 'fail' || lowerValue === 'false';
  };

  // Check if comments section should be shown
  const shouldShowCommentsSection = (): boolean => {
    return selectedValues.some(value => shouldShowRedChip(value));
  };

  // Check if comment input should be shown
  const shouldShowCommentInput = (): boolean => {
    // Don't show comment input in preview mode
    if (mode === 'preview') {
      return false;
    }
    const needsComments = shouldShowCommentsSection();
    const hasNoComment = !comment || comment.trim() === '';
    return needsComments && (showCommentInput || hasNoComment);
  };

  // Check if comment display should be shown
  const shouldShowCommentDisplay = (): boolean => {
    // In preview mode, show comments if they exist and showCommentsInPreview is true
    if (mode === 'preview' && showCommentsInPreview && comment) {
      return true;
    }
    return shouldShowCommentsSection() && !showCommentInput && !!comment;
  };

  // Toggle comment input visibility
  const toggleCommentInput = () => {
    // Don't allow toggling in preview mode
    if (mode === 'preview') {
      return;
    }
    setShowCommentInput(!showCommentInput);
  };

  // Handle comment save
  const handleCommentSave = (newComment: string) => {
    setComment(newComment);
    setShowCommentInput(false);
    
    if (onValueChange) {
      const validation = validateField(selectedValues);
      const change: IFormControlChange = {
        id,
        value: selectedValues,
        isValid: validation.isValid,
        errors: validation.errors,
        comments: newComment
      };
      onValueChange(change);
    }
  };

  // Handle checkbox change
  const handleCheckboxChange = (optionValue: string, checked: boolean) => {
    
    let newValues: string[];
    
    if (checked) {
      newValues = [...selectedValues, optionValue];
    } else {
      newValues = selectedValues.filter(value => value !== optionValue);
    }
    
    handleValueChange(newValues);
  };

  // Handle focus
  const handleFocus = () => {
    if (onFocus) {
      onFocus();
    }
  };

  // Handle blur
  const handleBlur = () => {
    setIsTouched(true);
    if (onBlur) {
      onBlur();
    }
  };

  // Update touched fields
  useEffect(() => {
    if (isTouched) {
      touchedFields[id] = true;
    }
  }, [isTouched, id, touchedFields]);

  // Log formValue changes for debugging
  useEffect(() => {
  }, [formValue, id, selectedValues]);

  // Mark as touched when form is submitted
  useEffect(() => {
    if (formSubmitted) {
      setIsTouched(true);
    }
  }, [formSubmitted]);

  // Get label alignment class
  const getLabelAlignmentClass = (): string => {
    const alignment = properties?.styles?.labelAlignment;
    return alignment === 'left' ? 'label-left' : 'label-top';
  };

  // Get layout class for inline/vertical layout
  const getLayoutClass = (): string => {
    return properties?.basic?.inlineLayout ? 'inline-layout' : 'vertical-layout';
  };


  const shouldDisable = disabled || readonly || properties?.validation?.readonly;

  return (
    <>
    <div className={`form-group checkbox-group ${getLabelAlignmentClass()} ${getLayoutClass()} ${className}`}>
      {!hideLabel && properties.basic.label ? (
        <>
          <label className="form-label">
            {properties.basic.label}
            {properties.validation.required && (
              <span className="required-indicator">*</span>
            )}
          </label>
          
          <div className="input-wrapper">
            <div className={`mt-2 ${properties?.basic?.inlineLayout ? 'inline-options-grid' : 'space-y-2'}`}>
              {(properties.options || []).map((option, index) => (
                <div key={index} className="form-check">
                  <input
                    type="checkbox"
                    id={`${id}-${index}`}
                    name={id}
                    value={option.value}
                    checked={selectedValues.includes(option.value)}
                    onChange={(e) => {
                      handleCheckboxChange(option.value, e.target.checked);
                    }}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    disabled={shouldDisable || option.disabled}
                    className="form-check-input"
                  />
                  <label 
                    htmlFor={`${id}-${index}`} 
                    className="form-check-label"
                  >
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
            <DfFormErrorMsg
        validationErrors={validationErrors}
        fieldId={id}
        touchedFields={touchedFields}
        formSubmitted={formSubmitted}
        properties={properties}
        localValidation={validateField(selectedValues)}
        isTouched={isTouched}
        mode={mode}
      />
          </div>
        </>
      ) : (
        <div className="input-wrapper">
          <div className={`mt-2 ${properties?.basic?.inlineLayout ? 'inline-options-grid' : 'space-y-2'}`}>
            {(properties.options || []).map((option, index) => (
              <div key={index} className="form-check">
                <input
                  type="checkbox"
                  id={`${id}-${index}`}
                  name={id}
                  value={option.value}
                  checked={selectedValues.includes(option.value)}
                  onChange={(e) => {
                    handleCheckboxChange(option.value, e.target.checked);
                  }}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  disabled={shouldDisable || option.disabled}
                  className="form-check-input"
                />
                <label 
                  htmlFor={`${id}-${index}`} 
                  className="form-check-label"
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
          <DfFormErrorMsg
        validationErrors={validationErrors}
        fieldId={id}
        touchedFields={touchedFields}
        formSubmitted={formSubmitted}
        properties={properties}
        localValidation={validateField(selectedValues)}
        isTouched={isTouched}
        mode={mode}
      />
        </div>
      )}



    </div>
          {/* Comments Section - Below checkbox options */}
          {(!shouldDisable || (mode === 'preview' && showCommentsInPreview)) && (
            <>
              {/* Show saved comment display when not in comment input mode */}
              {shouldShowCommentDisplay() && (
                <div onClick={mode === 'preview' ? undefined : toggleCommentInput} style={{ cursor: mode === 'preview' ? 'default' : 'pointer' }}>
                  <label className="form-label notes-label-text">
                    Notes: {comment}
                  </label>
                </div>
              )}
    
              {/* Show comment input when needed */}
              {shouldShowCommentInput() && (
                <DfFormComments
                  comment={comment}
                  onSave={handleCommentSave}
                  placeholder="Enter your reason..."
                  disabled={shouldDisable}
                />
              )}
            </>
          )}
          </>
  );
};

export default DfFormCheckbox;
